import { useTranslation } from "react-i18next"
import { Container, Row, Col, Card, Modal, Tabs, Tab, Table, Pagination as BsPagination } from 'react-bootstrap'
import { useAuth } from '../components/auth/useAuth'
import { useState, useRef,useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getDepartments, getMyTickets, sendTicket, replyTicket, getTicketMessages } from "../actions/odooActions"
import Select from "react-select"
import Swal from "sweetalert2"
import DOMPurify from "dompurify";
import Paginator from "../components/pagination/Paginator"
import TicketsTable from "../components/tables/tableTickets"
import imageCompression from 'browser-image-compression'

export default function Odoo() {
	const { t } = useTranslation()
	const { clientauth } = useAuth()
	const dispatch = useDispatch()

	const PAGE_SIZE = 25
	const [activeTab, setActiveTab] = useState('abiertos')
	const [pageOpen, setPageOpen] = useState(1)
	const [pageClosed, setPageClosed] = useState(1)
	const [search, setSearch] = useState('')
	const [submittedSearch, setSubmittedSearch] = useState('')
	const [sortField, setSortField] = useState('create_date')
	const [sortOrder, setSortOrder] = useState('desc')

	const [modalShow, setModalShow] = useState(false)

	const { departments, resultodoo, tickets, totalPages, messages, loadingTickets } = useSelector(state => state.odoo)

	const [selectedDepartment, setSelectedDepartment] = useState(null)
	const departmentOptions = departments?.map(dep => ({
		value: dep.id,
		label: dep.name
	}))

	const [selectedTicket, setSelectedTicket] = useState(null)
	const [reply, setReply] = useState("")
	const [files, setFiles] = useState([])
	const [previews, setPreviews] = useState([])
	const [loadedImages, setLoadedImages] = useState({})
	const ticketFileInputRef = useRef(null)
	const replyFileInputRef = useRef(null)

	const status = activeTab === 'abiertos' ? 'abiertos' : 'resueltos'
	const currentPage = activeTab === 'abiertos' ? pageOpen : pageClosed

	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: 'instant',
		})
		if (clientauth) {
			dispatch(getDepartments())
			dispatch(
				getMyTickets(
					currentPage,
					PAGE_SIZE,
					status,
					submittedSearch,
					sortField,
					sortOrder
				)
			)
		}
	}, [dispatch, t, clientauth, currentPage, status, submittedSearch, sortField, sortOrder])

	useEffect(() => {
		setPageOpen(1)
		setPageClosed(1)
	}, [submittedSearch])

	useEffect(() => {
		return () => {
			previews.forEach(url => URL.revokeObjectURL(url))
		}
	}, [previews])

	const optimizeImages = async (fileList) => {
		try {
			const options = {
				maxSizeMB: 0.3,
				maxWidthOrHeight: 1600,
				useWebWorker: true
			}
			const optimizedFiles = await Promise.all(
				Array.from(fileList).map(async (file) => {
					if (!file.type.startsWith('image/')) {
						return file
					}
					const compressedFile = await imageCompression(file, options)
					return new File(
						[compressedFile],
						file.name,
						{
							type: compressedFile.type
						}
					)
				})
			)
			setFiles(optimizedFiles)
			const previewUrls = optimizedFiles.map(file =>
				URL.createObjectURL(file)
			)
			setPreviews(previewUrls)
		} catch (error) {
			console.error(error)
			Swal.fire({
				icon: 'error',
				text: t('global.error')
			})
		}
	}

	const [form, setForm] = useState({
		subject: "",
		department: "",
		priority: "",
		description: ""
	})

	const [showReplyForm, setShowReplyForm] = useState(false)
	const currentTicket = tickets?.find(
		t => t.id === selectedTicket
	)
	const isClosedTicket = currentTicket?.stage_id?.[1] === 'Solved' || currentTicket?.stage_id?.[1] === 'Cancelled'

	// Función encargada de limpiar el HTML
	const cleanTicketDescription = (html) => {
		if (!html) return "";
		// 1. Cortar el email (firma / respuestas)
		let cleaned = html.split('<hr')[0];
		// 2. Sanitizar (quitar cosas peligrosas y basura)
		cleaned = DOMPurify.sanitize(cleaned, {
			ALLOWED_TAGS: [
				'b', 'i', 'em', 'strong', 'p', 'br',
				'ul', 'ol', 'li', 'span'
			],
			ALLOWED_ATTR: []
		});
		return cleaned;
	};
	// Función encargada de renderizar las estrellas
	const renderStars = (priority) => {
		const max = 3;
		const value = Number(priority || 0);
		let stars = [];
		for (let i = 0; i < max; i++) {
			if (i < value) {
				stars.push(<i key={i} className="fas fa-star text-warning" style={{ marginRight: "2px" }}></i>);
			} else {
				stars.push(<i key={i} className="far fa-star text-secondary" style={{ marginRight: "2px" }}></i>);
			}
		}
		return stars;
	};
	// Función encargada de renderizar el color del estado
	const getStageClass = (stageId) => {
		switch (stageId) {
			case 1: return "bg-info"; // Nuevo
			case 2: return "bg-warning";   // Progreso
			case 3: return "bg-primary";      // Espera soporte
			case 4: return "bg-success";   // Resuelto
			default: return "bg-danger";
		}
	};
	const handleChange = (e) => {
		setForm({
			...form,
			[e.target.name]: e.target.value
		})
	}

	const handleSubmit = (e) => {
		e.preventDefault()
		const formData = new FormData()
		formData.append("subject", form.subject)
		formData.append("description", form.description)
		formData.append("priority", form.priority)
		formData.append("department", form.department)
		files.forEach(file => {
			formData.append("files", file)
		})
		dispatch(sendTicket(formData))
		setForm({
			subject: "",
			department: "",
			priority: "",
			description: ""
		})
		setSelectedDepartment(null)
		setFiles([])
		setPreviews([])
		if (ticketFileInputRef.current) {
			ticketFileInputRef.current.value = null
		}
	}

	const handleTicketClick = (ticket) => {
		setSelectedTicket(prev => prev === ticket.id ? null : ticket.id)
		dispatch({ type: 'CLEAR_MESSAGES' })
		dispatch(getTicketMessages(ticket.id))
		setModalShow(true)
	}

	const handleImageClick = (e, ticket) => {
		e.stopPropagation()
		handleTicketClick(ticket)
	}

	useEffect(() => {
		if (resultodoo) {
			let resultodoonow = resultodoo
			dispatch({ type: 'ODOO_RESET' })
			switch (resultodoonow) {
				case 'addSuccess':
					Swal.fire({
						showConfirmButton: true,
						showDenyButton: false,
						confirmButtonText: t('global.great'),
						icon: 'success',
						text: t('ticketsTranslate.addsuccess')
					})

					break;
				case 'needMoreData':
					Swal.fire({
						showConfirmButton: true,
						showDenyButton: false,
						confirmButtonText: t('global.ok'),
						icon: 'warning',
						text: t('ticketsTranslate.needMoreData')
					})
					break;
				case 'replySuccess':
					Swal.fire({
						showConfirmButton: true,
						showDenyButton: false,
						confirmButtonText: t('global.great'),
						icon: 'success',
						text: t('ticketsTranslate.replysuccess')
					}).then(() => {
						const status = activeTab === 'abiertos' ? 'abiertos' : 'resueltos'
						const currentPage = activeTab === 'abiertos' ? pageOpen : pageClosed
						dispatch(getMyTickets(currentPage, PAGE_SIZE, status))
					})
					break;
				default:
					break;
			}
		}
		// eslint-disable-next-line
	}, [resultodoo])

	return (
		<>
			<Container fluid className='contenido p-5'>

				<h2 className="mb-1">{t("ticketsTranslate.tickets")}</h2>
				<p className="text-muted mb-4">
					{t("ticketsTranslate.management")}
				</p>

				{/* Crear ticket */}

				<div className="card mb-5">
					<div className="card-body">

						<h5 className="mb-3">{t("ticketsTranslate.create")}</h5>

						<form onSubmit={handleSubmit}>

							<div className="mb-3">
								<label className="form-label">{t("ticketsTranslate.issue")}</label>
								<input
									type="text"
									name="subject"
									className="form-control"
									placeholder={t("ticketsTranslate.issueDescription")}
									value={form.subject}
									onChange={handleChange}
								/>
							</div>

							<div className="row">

								<div className="col-md-6 mb-3">
									<label className="form-label">{t("ticketsTranslate.departament")}</label>
									<Select
										options={departmentOptions}
										value={selectedDepartment}
										onChange={(option) => {
											setSelectedDepartment(option)
											setForm({
												...form,
												department: option.value
											})
										}}
										placeholder={t("ticketsTranslate.selectDepartament")}
									/>
								</div>

								<div className="col-md-6 mb-3">
									<label className="form-label">{t("ticketsTranslate.priority")}</label>
									<select
										name="priority"
										className="form-control"
										value={form.priority}
										onChange={handleChange}
									>
										<option value="">{t("ticketsTranslate.selectPriority")}</option>
										<option value="1">{t("ticketsTranslate.priorityLow")}</option>
										<option value="2">{t("ticketsTranslate.priorityMedium")}</option>
										<option value="3">{t("ticketsTranslate.priorityHigh")}</option>
									</select>
								</div>

							</div>

							<div className="mb-3">
								<label className="form-label">{t("ticketsTranslate.description")}</label>
								<textarea
									name="description"
									className="form-control"
									rows="4"
									placeholder={t("ticketsTranslate.descriptionPlaceholder")}
									value={form.description}
									onChange={handleChange}
								/>
								<input
									ref={ticketFileInputRef}
									type="file"
									accept="image/*"
									multiple
									className="form-control mt-2"
									onChange={(e) => optimizeImages(e.target.files)}
								/>
								{previews.length > 0 && (
									<div className="d-flex flex-wrap gap-2 mt-2">
										{previews.map((preview, index) => (
											<img
												key={index}
												src={preview}
												alt="preview"
												style={{
													width: "120px",
													height: "120px",
													objectFit: "cover",
													borderRadius: "8px",
													border: "1px solid #ddd"
												}}
											/>
										))}
									</div>
								)}
							</div>

							<button className="btn btn-dark">
								{t("ticketsTranslate.sendTicket")}
							</button>

						</form>

					</div>
				</div>

				<Tabs
					activeKey={activeTab}
					onSelect={(k) => setActiveTab(k)}
					className="p-0"
				>
					<Tab eventKey="abiertos" title={t("ticketsTranslate.myTicketsOpen")} className="p-3">
						<TicketsTable
							tickets={tickets}
							loadingTickets={loadingTickets}
							page={pageOpen}
							onPageChange={setPageOpen}
							activeTab={activeTab}
							currentTab="abiertos"
							titleEmpty={t("ticketsTranslate.noOpenTickets")}
							handleTicketClick={handleTicketClick}
							handleImageClick={handleImageClick}
							renderStars={renderStars}
							getStageClass={getStageClass}
							totalPages={totalPages}
							search={search}
							setSearch={setSearch}
							submittedSearch={submittedSearch}
							setSubmittedSearch={setSubmittedSearch}
							sortField={sortField}
							sortOrder={sortOrder}
							setSortField={setSortField}
							setSortOrder={setSortOrder}
						/>
					</Tab>
					<Tab eventKey="resueltos" title={t("ticketsTranslate.myTicketsClosed")} className="p-3">
						<TicketsTable
							tickets={tickets}
							page={pageClosed}
							onPageChange={setPageClosed}
							activeTab={activeTab}
							currentTab="resueltos"
							titleEmpty={t("ticketsTranslate.noClosedTickets")}
							handleTicketClick={handleTicketClick}
							handleImageClick={handleImageClick}
							renderStars={renderStars}
							getStageClass={getStageClass}
							totalPages={totalPages}
							search={search}
							setSearch={setSearch}
							submittedSearch={submittedSearch}
							setSubmittedSearch={setSubmittedSearch}
							sortField={sortField}
							sortOrder={sortOrder}
							setSortField={setSortField}
							setSortOrder={setSortOrder}
						/>
					</Tab>
				</Tabs>
			</Container>
			<Modal
				show={modalShow}
				onHide={() => {
					setModalShow(false)
					setSelectedTicket(null)
					setReply("")
					setFiles([])
					setPreviews([])
					setLoadedImages({})
					setShowReplyForm(false)
				}}
				aria-labelledby="contained-modal-title-vcenter"
				size="lg"
				centered
			>
				<Modal.Header closeButton>
					<Modal.Title id="contained-modal-title-vcenter">
						<h3>{tickets?.find(t => t.id === selectedTicket)?.name}</h3>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body className="p-4">
					{selectedTicket && tickets?.some(t => t.id === selectedTicket) && (
						<>
							<div className="my-2 p-3 border rounded ticket-description">
								<h4>{t("ticketsTranslate.description")}</h4>
								<hr />
								<div
									dangerouslySetInnerHTML={{
										__html: DOMPurify.sanitize(tickets.find(t => t.id === selectedTicket).description)
									}}
								/>
							</div>
							{/* Hilo de mensajes */}
							{messages?.length === 0 && (
								<div className="text-muted text-center py-3">
									{t("ticketsTranslate.noMessages")}
								</div>
							)}
							{messages
								?.filter(msg => {
									const text = (msg.body || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim()
									return text.length > 0 || (msg.attachment_ids?.length || 0) > 0
								})
								.sort((a, b) => new Date(a.date) - new Date(b.date))
								.map(msg => (
									<div key={msg.id} className="my-2 p-3 border rounded ticket-description">
										<div
											dangerouslySetInnerHTML={{
												__html: DOMPurify.sanitize(msg.body)
											}}
										/>

										<div className="mb-1">
											<strong>
												{msg.author_id?.[1] || "Sistema"}
											</strong>
										</div>

										<small className="text-muted">
											{new Date(msg.date).toLocaleString('es-ES', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric'
                                            })}
										</small>

										{msg.attachment_ids?.length > 0 && (
											<div className="mt-2">
												{msg.attachment_ids.map(attId => {
													const isLoaded = loadedImages[attId]
													return (
														<div key={attId} className="mb-2">
															{!isLoaded ? (
																<button
																	className="btn btn-sm btn-outline-primary"
																	onClick={() => {
																		setLoadedImages(prev => ({
																			...prev,
																			[attId]: true
																		}))
																	}}
																>
																	{t("ticketsTranslate.viewImage")}
																</button>
															) : (
																<img
																	src={`${process.env.REACT_APP_DB_ADDRESS}/api/odoo/attachment/${attId}`}
																	alt="attachment"
																	style={{
																		maxWidth: "200px",
																		borderRadius: "8px",
																		border: "1px solid #ddd",
																		display: "block",
																		marginTop: "5px"
																	}}
																/>
															)}
														</div>
													)
												})}
											</div>
										)}
									</div>
								))
							}

							<div className="my-4">
								{isClosedTicket && !showReplyForm ? (
									<div className="text-center">
										<div className="alert alert-warning">
											{t("ticketsTranslate.ticketClosed")}
										</div>
										<button
											className="btn btn-outline-dark"
											onClick={() => setShowReplyForm(true)}
										>
											{t("ticketsTranslate.reopenTicket")}
										</button>
									</div>
								) : (
									<>
										<textarea
											className="form-control mb-2"
											rows="3"
											placeholder={t("ticketsTranslate.replyPlaceholder")}
											value={reply}
											onChange={(e) => setReply(e.target.value)}
										/>
										<input
											ref={replyFileInputRef}
											type="file"
											accept="image/*"
											multiple
											className="form-control mt-2"
											onChange={(e) => optimizeImages(e.target.files)}
										/>
										{previews.length > 0 && (
											<div className="d-flex flex-wrap gap-2 mt-2">
												{previews.map((preview, index) => (
													<img
														key={index}
														src={preview}
														alt="preview"
														style={{
															width: "120px",
															height: "120px",
															objectFit: "cover",
															borderRadius: "8px",
															border: "1px solid #ddd"
														}}
													/>
												))}
											</div>
										)}
										<button
											className="btn btn-dark btn-sm mt-2"
											onClick={() => {
												const formData = new FormData()
												formData.append("ticket_id", selectedTicket)
												formData.append("message", reply)
												files?.forEach(file => {
													formData.append("files", file)
												})
												dispatch(replyTicket(formData))
												setReply("")
												setFiles([])
												setPreviews([])
												if (replyFileInputRef.current) {
													replyFileInputRef.current.value = null
												}
												setSelectedTicket(null)
												setModalShow(false)
												setShowReplyForm(false)
											}}
											disabled={!reply.trim()}
										>
											{t("ticketsTranslate.sendReply")}
										</button>
									</>
								)}
							</div>
						</>
					)}
				</Modal.Body>
			</Modal>
		</>
	)
}